<template>
  <Page>
    <SpinnerBackdrop
      message="Creating your batch, please wait..."
      :spinning="!!pendingBackgroundTaskId"
    >
      <TabSwitcher
        id="ub"
        ref="tabSwitcher"
        :tabs="tabs"
        render-all
        @tab-change="initTippy"
      >
        <template #card-header-top>
          <div
            class="d-flex flex-row flex-wrap items-start pb-0 mb-3 justify-content-between page-header-content"
          >
            <div class="d-flex align-items-center">
              <h1 class="page-header-1">Unbatched</h1>
            </div>
            <div class="flex w-full actions-section">
              <div
                class="flex flex-col gap-3 md:flex-row md:items-end md:flex-wrap datatable-report-actions w-full"
              >
                <div v-for="filter in globalFilters" :key="filter.name">
                  <DatatableFilter
                    :filter="filter"
                    @refresh-table="clearSelectionsAndRefresh"
                  />
                </div>
                <div
                  class="flex justify-evenly batch-summary-stats rounded py-2 px-3 mr-2"
                >
                  <div
                    class="flex flex-col md:flex-row md:flex-wrap items-center gap-1"
                  >
                    Total Invoices:
                    <strong>{{ invoiceTotals }}</strong>
                  </div>
                  <divider vertical class="border-dark" />
                  <div
                    class="flex flex-col md:flex-row md:flex-wrap items-center gap-1"
                  >
                    Total Payments:
                    <strong>{{ paymentAmounts }}</strong>
                  </div>
                </div>
                <Button
                  v-if="processor === 'qbo'"
                  :disabled="batchButtonDisabled"
                  class="download-report-btn"
                  @click="toggleModal()"
                >
                  <i
                    v-if="waitingOnSelections"
                    class="far fa-circle-notch spinner mr-1"
                  ></i>
                  <i v-else class="fas fa-inbox-out mr-1"></i>
                  Send to QuickBooks
                </Button>
                <Button
                  v-else
                  :disabled="batchButtonDisabled"
                  class="download-report-btn"
                  @click="toggleModal()"
                >
                  <i
                    v-if="waitingOnSelections"
                    class="far fa-circle-notch spinner mr-1"
                  ></i>
                  <i v-else class="fas fa-download mr-1"></i>
                  Batch & Download
                </Button>
              </div>
            </div>
          </div>
          <div v-if="qboNotReady" class="mb-4">
            <Alert type="error">
              <span>
                QuickBooks configuration has an error that needs resolved:
                <span class="reason">{{ qboDisabledReason }}</span>
                <span class="ml-2"
                  ><a href="/marketplace?card=qbo">Click here</a> to
                  configure.</span
                >
              </span>
            </Alert>
          </div>
          <div
            v-if="showAccountingClasses && notReadyAccountingClassesCount > 0"
            class="mb-4"
          >
            <Alert type="warning">
              <span>
                {{ notReadyAccountingClassesMessage }}
                <i
                  ><b>Note:</b> It is recommended to assign classes to invoices
                  before exporting to QuickBooks.</i
                >
              </span>
            </Alert>
          </div>
        </template>
      </TabSwitcher>
    </SpinnerBackdrop>
    <Modal
      v-if="modalOpen"
      title="Create Batch"
      :width="500"
      :saving="saving"
      submit-btn-text="Create Batch"
      @hide-modal="toggleModal(false)"
      @submit="handleSubmit"
    >
      <div class="d-flex full-width">
        <TextField
          v-model="batchName"
          :validation-errors="v$.$errors"
          name="Batch Name"
          class="flex-1"
        />
      </div>

      <div
        v-if="includesSelectedUnbatchablePayments"
        class="warning-message mt-2"
      >
        <i class="far fa-exclamation-triangle mr-1"></i>
        <div>
          <strong
            >Unable to batch {{ selectedUnbatchablePaymentsCount }} selected
            payment</strong
          >
          update(s) without batching the following invoice(s):
          <ul class="mb-0">
            <li
              v-for="(invoiceUrl, invoiceId) in invoiceLinks"
              :key="invoiceId"
            >
              <a :href="invoiceUrl" target="_blank">#{{ invoiceId }}</a>
            </li>
          </ul>
        </div>
      </div>

      <div v-if="warnNoPaymentsSelected" class="notice-message mt-2">
        <i class="far fa-circle-info mr-1"></i>
        <div>
          There are <strong>no payments selected</strong> for this batch.
        </div>
      </div>

      <div class="d-flex batch-summary-stats rounded mt-2 py-2 px-2">
        Total Invoices ({{ selectedInvoiceIds.length }}):&nbsp;
        <strong>{{ invoiceTotals }}</strong>
        <divider vertical class="border-dark" />
        Total Payments ({{ selectedPaymentIds.length }}):&nbsp;
        <strong>{{ paymentAmounts }}</strong>
      </div>
    </Modal>
  </Page>
</template>

<script setup lang="ts">
import debounce from "lodash/debounce";
import API from "serviceshift-ui/api-client";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  watch
} from "vue";
import { useStore } from "vuex";
import cable from "@/lib/cable";
import { EventBus } from "@/lib/event_bus";
import { authenticityToken } from "@/lib/helpers";

import Button from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import Page from "@/components/Page.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import SpinnerBackdrop from "@/components/SpinnerBackdrop.vue";
import TabSwitcher from "@/components/TabSwitcher/TabSwitcher.vue";
import TextField from "@/components/TextField.vue";
import VueChannel from "@/channels/VueChannel";
import DatatableFilter from "@/Reports/DatatableFilter.vue";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { getQueryVariables } from "../lib/url.js";
import UnbatchedInvoices from "./UnbatchedInvoices.vue";
import PaymentExports from "./UnbatchedPayments.vue";

const props = withDefaults(
  defineProps<{
    invoicesSource?: string;
    paymentsSource?: string;
    invoiceFilters?: Array<any>;
    paymentFilters?: Array<any>;
    globalFilters?: Array<any>;
    initialBatchName?: string;
  }>(),
  {
    invoicesSource: "",
    paymentsSource: "",
    invoiceFilters: () => [],
    paymentFilters: () => [],
    globalFilters: () => [],
    initialBatchName: ""
  }
);

const store = useStore();

const channel = new VueChannel();

const selectedInvoiceIds = ref<number[]>([]);
const selectedPaymentIds = ref<number[]>([]);
const invoiceTotals = ref<string>("None Selected");
const paymentAmounts = ref<string>("None Selected");
const modalOpen = ref<boolean>(false);
const saving = ref<boolean>(false);
const batchName = ref<string>(props.initialBatchName);
const batchNameDefault = ref<string>(props.initialBatchName);
const selectedUnbatchablePaymentsCount = ref<number>(0);
const invoiceLinks = ref<Record<string, string>>({});
const warnNoPaymentsSelected = ref<boolean>(false);
const pendingBackgroundTaskId = ref<string | null>(null);
const pendingRefreshCalls = ref<number>(0);
const refreshingBatchTotals = ref<boolean>(false);
const pendingInvoiceRefresh = ref<boolean>(true);
const pendingPaymentRefresh = ref<boolean>(true);
const notReadyAccountingClassesCount = ref<number>(0);

const tabSwitcher = ref<InstanceType<typeof TabSwitcher> | null>(null);

const showAccountingClasses = computed(
  () => store.getters.showAccountingClasses
);
const tenant = computed(() => store.state.tenant);
const backgroundTasks = computed(() => store.state.backgroundTasks);
const fetchingTenant = computed(() => store.state.fetchingTenant);

const processor = computed(() => {
  if (!tenant.value) return null;
  return tenant.value.accounting_processor;
});

const qboEnabled = computed(() => {
  if (!tenant.value) return false;
  if (!tenant.value.qbo_config) return false;
  return tenant.value.qbo_config.enabled;
});

const qboDisabledReason = computed(() => {
  if (!tenant.value) return null;
  if (!tenant.value.qbo_config) return null;
  return tenant.value.qbo_config.disabled_reason;
});

const qboNotReady = computed(() => {
  return (
    processor.value === "qbo" && !qboEnabled.value && !!qboDisabledReason.value
  );
});

const batchButtonDisabled = computed(() => {
  if (qboNotReady.value) {
    return true;
  }

  const noSelections =
    selectedInvoiceIds.value.length === 0 &&
    selectedPaymentIds.value.length === 0;

  return noSelections || waitingOnSelections.value;
});

const invoiceTabDisplayName = computed(() => {
  if (selectedInvoiceIds.value.length === 0) return "Invoices";
  return `Invoices (${selectedInvoiceIds.value.length})`;
});

const paymentTabDisplayName = computed(() => {
  if (selectedPaymentIds.value.length === 0) return "Payments";
  return `Payments (${selectedPaymentIds.value.length})`;
});

const waitingOnSelections = computed(() => {
  return (
    pendingRefreshCalls.value > 0 ||
    refreshingBatchTotals.value ||
    pendingInvoiceRefresh.value ||
    pendingPaymentRefresh.value
  );
});

const includesSelectedUnbatchablePayments = computed(() => {
  return selectedUnbatchablePaymentsCount.value !== 0;
});

const notReadyAccountingClassesMessage = computed(() => {
  const count = notReadyAccountingClassesCount.value;
  return (
    "There " +
    (count === 1
      ? "is 1 selected invoice"
      : "are " + count + " selected invoices") +
    " with missing or invalid accounting classes."
  );
});

const tabs = computed(() => {
  return [
    {
      id: "invoiceTab",
      name: "Invoices",
      displayName: invoiceTabDisplayName.value,
      component: UnbatchedInvoices,
      ref: "invoiceTab",
      props: {
        source: props.invoicesSource,
        filters: [...props.invoiceFilters, ...props.globalFilters],
        qboEnabled: qboEnabled.value
      }
    },
    {
      id: "paymentTab",
      name: "Payments",
      displayName: paymentTabDisplayName.value,
      component: PaymentExports,
      ref: "paymentTab",
      props: {
        source: props.paymentsSource,
        filters: [...props.paymentFilters, ...props.globalFilters],
        qboEnabled: qboEnabled.value
      }
    }
  ];
});

const v$ = useVuelidate(
  {
    batchName: { required, $autoDirty: true }
  },
  { batchName }
);

const updateSelectedInvoiceIds = (ids: number[]) => {
  selectedInvoiceIds.value = ids;
};

const updateSelectedPaymentIds = (ids: number[]) => {
  selectedPaymentIds.value = ids;
};

const handleUpdateBatchTotals = () => {
  refreshingBatchTotals.value = true;
  updateBatchTotals();
};

const updateBatchTotals = debounce(async () => {
  try {
    pendingRefreshCalls.value += 1;
    const res = await API.makeRequest("/accounting/unbatched/totals", {
      method: "POST",
      data: {
        invoice_ids: selectedInvoiceIds.value,
        payment_ids: selectedPaymentIds.value,
        ...getQueryVariables()
      },
      headers: { "X-CSRF-Token": authenticityToken() }
    });

    invoiceTotals.value = res.data.invoice_totals;
    paymentAmounts.value = res.data.payment_amounts;
    selectedUnbatchablePaymentsCount.value =
      res.data.selected_unbatchable_payments_count;
    invoiceLinks.value = res.data.invoice_links;
    warnNoPaymentsSelected.value = res.data.warn_no_payments_selected;
    batchNameDefault.value = res.data.batch_name;
    notReadyAccountingClassesCount.value =
      res.data.not_ready_accounting_classes_count;
  } finally {
    pendingRefreshCalls.value -= 1;

    if (pendingRefreshCalls.value === 0) {
      refreshingBatchTotals.value = false;
    }
  }
}, 500);

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  saving.value = true;

  try {
    const res = await API.makeRequest("/reports/accounting", {
      method: "POST",
      data: {
        name: batchName.value,
        invoice_ids: selectedInvoiceIds.value,
        payment_ids: selectedPaymentIds.value
      }
    });

    pendingBackgroundTaskId.value = res.data.background_task_id;

    toggleModal(false);
    clearSelections();
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error batching", error);
  }

  saving.value = false;
};

const initTippy = () => {
  nextTick(() => {
    tippy("[data-tippy-content]");
    document.dispatchEvent(new CustomEvent("scroll"));
    window.dispatchEvent(new CustomEvent("resize"));
  });
};

const clearSelectionsAndRefresh = () => {
  clearSelections();
  refreshDatatable();
};

const refreshDatatable = () => {
  pendingInvoiceRefresh.value = true;
  pendingPaymentRefresh.value = true;
  tabSwitcher.value?.$refs.invoiceTab[0].refreshDataAndSelectAll();
  tabSwitcher.value?.$refs.paymentTab[0].refreshDataAndSelectAll();
};

const toggleModal = (newValue?: boolean) => {
  modalOpen.value = newValue !== undefined ? newValue : !modalOpen.value;
};

const clearSelections = () => {
  EventBus.$emit("clear-datatable-bulk-select");
};

const checkPendingBackgroundTaskStatus = () => {
  if (pendingBackgroundTaskId.value === null) return;

  const pendingBackgroundTask = backgroundTasks.value.find(
    (task: any) => task.id === pendingBackgroundTaskId.value
  );

  if (pendingBackgroundTask?.detailsUrl) {
    window.location.href = pendingBackgroundTask.detailsUrl;
  }

  if (pendingBackgroundTask?.isCompleted) {
    pendingBackgroundTaskId.value = null;
  }
};

const resolvePendingInvoiceRefresh = () => {
  pendingInvoiceRefresh.value = false;
};

const resolvePendingPaymentRefresh = () => {
  pendingPaymentRefresh.value = false;
};

async function listenForCableUpdates() {
  try {
    await cable.subscribe(channel);
  } catch (err) {
    console.log("App Unable to join Cable server", err);
  }
}

watch(selectedInvoiceIds, () => {
  handleUpdateBatchTotals();
});

watch(selectedPaymentIds, () => {
  handleUpdateBatchTotals();
});

watch(backgroundTasks, () => {
  checkPendingBackgroundTaskStatus();
});

watch(pendingBackgroundTaskId, (newVal, oldVal) => {
  if (newVal === oldVal) return;
  if (newVal === null) {
    refreshDatatable();
  } else {
    checkPendingBackgroundTaskStatus();

    if (!qboEnabled.value) {
      setTimeout(() => {
        pendingBackgroundTaskId.value = null;
      }, 15000);
    }
  }
});

watch(batchNameDefault, (newVal) => {
  batchName.value = newVal;
});

watch(modalOpen, (newVal) => {
  if (newVal === true) {
    batchName.value = batchNameDefault.value;
  }
});

onMounted(() => {
  EventBus.$on("refresh-invoice-totals", handleUpdateBatchTotals);

  // Fetch tenant if needed (equivalent to created hook)
  if (!tenant.value && !fetchingTenant.value) {
    store.dispatch("fetchTenant");
  }

  listenForCableUpdates();
});

onBeforeUnmount(() => {
  EventBus.$off("refresh-invoice-totals", handleUpdateBatchTotals);
  channel.disconnect();
});

provide("selectedInvoiceIds", selectedInvoiceIds);
provide("updateSelectedInvoiceIds", updateSelectedInvoiceIds);
provide("selectedPaymentIds", selectedPaymentIds);
provide("updateSelectedPaymentIds", updateSelectedPaymentIds);
provide("resolvePendingInvoiceRefresh", resolvePendingInvoiceRefresh);
provide("resolvePendingPaymentRefresh", resolvePendingPaymentRefresh);
</script>

<style scoped lang="scss">
//removes unbatched reports default padding
div#invoice_exports_report.container-fluid,
div#payment_exports_report.container-fluid {
  :deep(main) {
    padding: 0 !important;
  }
}

.batch-summary-stats {
  background-color: var(--angel-blue);
}

.warning-message,
.notice-message {
  display: flex;
  padding: 5px 10px;
  border-radius: 0.25rem;

  i {
    margin-top: 3px;
    font-size: 1.1em;
  }
}

.warning-message {
  background: var(--warning-color);
}

.notice-message {
  background: var(--uranian-blue);
}

.reason {
  font-weight: bold;
}

:deep(.datatable-v2 .global-filters) {
  z-index: unset;
}

:deep(.batch-type-filter-width) {
  min-width: 90px;
}

:deep(.accounting-classes-ready-filter-width) {
  min-width: 90px;
}
</style>
